import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { AlertTriangle, Shield, Scale, Eye, Lock } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_locate-loved-ones-3/artifacts/5wufybp4_LOGO.jpeg";

export default function ConsentPage() {
  const { user, acceptConsent } = useAuth();
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAccept = async () => {
    if (!agreed) {
      setError('You must agree to the terms to continue');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await acceptConsent();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError('Failed to save consent. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = () => {
    navigate('/', { replace: true });
  };

  if (user?.consent_accepted) {
    navigate('/dashboard', { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2">
            <img src={LOGO_URL} alt="MISYOU" className="w-12 h-12 rounded-xl object-cover" />
            <span className="text-2xl font-black text-slate-900">MISYOU</span>
          </div>
        </div>

        <Card className="shadow-xl shadow-slate-200/50 border-slate-100">
          <CardHeader className="text-center pb-4 border-b border-slate-100">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-amber-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900">
              UPLOAD AT YOUR OWN RISK
            </CardTitle>
            <p className="text-muted-foreground mt-2">
              Please read and accept the following terms before proceeding
            </p>
          </CardHeader>

          <CardContent className="pt-6 space-y-6">
            {/* Main Consent Text */}
            <Alert className="bg-amber-50 border-amber-200">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <AlertTitle className="text-amber-800 font-semibold">Important Notice</AlertTitle>
              <AlertDescription className="text-amber-700 mt-2">
                This app has been designed and introduced with a noble intention to help find people 
                their missing loved ones. However, in this age of data and photo leaks, please upload 
                any data at your own risk. If you consider that your data should never be compromised, 
                we cannot guarantee its absolute security - please exercise caution when uploading.
              </AlertDescription>
            </Alert>

            {/* Key Points */}
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                  <Eye className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Public Visibility</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    This is a public app which functions when everybody sees your data and information 
                    uploaded by you. Information you submit may be visible to other users and the general public.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                  <Shield className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Data Protection</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    While we implement security measures, we cannot guarantee absolute protection 
                    against all data breaches or unauthorized access. Please be mindful of the 
                    information you share.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                  <Scale className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Legal Agreement</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    By accepting this consent, you agree not to raise any court litigation of any 
                    sort against the admins, their staff, or representatives at any stage whatsoever 
                    regarding data handling, privacy, or any other matter related to this platform.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                  <Lock className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Your Responsibility</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    You are solely responsible for the accuracy and appropriateness of the information 
                    you upload. Misuse of this platform, including false reports, is strictly prohibited 
                    and may result in account termination.
                  </p>
                </div>
              </div>
            </div>

            {/* Checkbox Agreement */}
            <div className="border-t border-slate-200 pt-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={agreed}
                  onCheckedChange={(checked) => setAgreed(checked)}
                  className="mt-1"
                  data-testid="consent-checkbox"
                />
                <span className="text-sm text-slate-700 leading-relaxed">
                  I have read, understood, and agree to the above terms. I acknowledge that I am 
                  uploading data at my own risk and agree not to pursue any legal action against 
                  MISYOU, its administrators, staff, or representatives.
                </span>
              </label>

              {error && (
                <p className="text-red-600 text-sm mt-2">{error}</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4">
              <Button
                variant="outline"
                className="flex-1 h-12 rounded-xl"
                onClick={handleDecline}
                data-testid="consent-decline"
              >
                Decline & Exit
              </Button>
              <Button
                className="flex-1 h-12 bg-primary hover:bg-primary/90 rounded-xl btn-press"
                onClick={handleAccept}
                disabled={!agreed || loading}
                data-testid="consent-accept"
              >
                {loading ? 'Processing...' : 'I Accept & Continue'}
              </Button>
            </div>

            <p className="text-center text-xs text-muted-foreground">
              This consent is mandatory. If you do not accept, you cannot use this application.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
